/**
 * Column class for defining table column configuration
 * Handles data access, rendering, sorting, filtering, and display properties
 */
import { columnFilterHasValue, rowPassesColumnFilter } from './tableDataUtils.js';

export class Column {
  /**
   * @param {Object} config - Column configuration
   * @param {string} config.key - Unique identifier for the column
   * @param {string} config.title - Display title for the column header
   * @param {Function} config.accessor - Function to extract data from row object
   * @param {Function} [config.renderer] - Optional custom renderer function for cell content
   * @param {Function} [config.compareFn] - Optional custom comparison function for sorting
   * @param {boolean} [config.sortable=true] - Whether this column can be sorted
   * @param {boolean} [config.filterable=true] - Whether this column can be filtered
   * @param {string} [config.dataType='text'] - Data type for filtering ('text', 'date', 'enum')
   * @param {Array} [config.enumOptions] - Options for enum type filters
   * @param {string} [config.className] - CSS class name for the column
   * @param {Object} [config.headerProps] - Additional props for the header cell
   * @param {Object} [config.cellProps] - Additional props for data cells
   * @param {boolean} [config.compulsory=false] - Whether this column is compulsory (always visible)
   */
  constructor(config) {
    this.key = config.key;
    this.title = config.title;
    this.accessor = config.accessor;
    this.renderer = config.renderer || ((value, row, column) => value);
    this.compareFn = config.compareFn || this.getDefaultCompareFn();
    this.sortable = config.sortable !== false; // Default to true unless explicitly false
    this.filterable = config.filterable !== false; // Default to true unless explicitly false
    this.dataType = config.dataType || 'text'; // Default to text
    this.enumOptions = config.enumOptions || [];
    this.className = config.className || '';
    this.headerProps = config.headerProps || {};
    this.cellProps = config.cellProps || {};
    this.compulsory = config.compulsory || false; // Default to false - column is optional
    
    // Filter state
    this.filterValue = null;
  }

  /**
   * Extract data from a row object using the accessor function
   * @param {Object} row - The data row
   * @returns {*} - The extracted value
   */
  getValue(row) {
    return this.accessor(row);
  }

  /**
   * Render cell content
   * @param {Object} row - The data row
   * @param {number} rowIndex - The row index
   * @returns {React.ReactNode} - The rendered cell content
   */
  renderCell(row, rowIndex) {
    const value = this.getValue(row);
    return this.renderer(value, row, this, rowIndex);
  }

  /**
   * Set filter value for this column
   * @param {*} value - The filter value
   */
  setFilterValue(value) {
    this.filterValue = value;
  }

  /**
   * Get current filter value
   * @returns {*} - The current filter value
   */
  getFilterValue() {
    return this.filterValue;
  }

  /**
   * Check if filter is active (has a value)
   * @returns {boolean} - Whether filter is active
   */
  hasActiveFilter() {
    return columnFilterHasValue(this, this.filterValue);
  }

  /**
   * Apply filter to a row
   * @param {Object} row - The data row
   * @returns {boolean} - Whether the row passes the filter
   */
  applyFilter(row) {
    if (!this.hasActiveFilter()) {
      return true;
    }

    return rowPassesColumnFilter(row, this, this.filterValue);
  }

  /**
   * Get default comparison function based on the data type
   * @returns {Function} - Comparison function for sorting
   */
  getDefaultCompareFn() {
    return (a, b) => {
      const valueA = this.getValue(a);
      const valueB = this.getValue(b);

      // Handle null/undefined values
      if (valueA == null && valueB == null) return 0;
      if (valueA == null) return 1;
      if (valueB == null) return -1;

      // Handle strings
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return valueA.toLowerCase().localeCompare(valueB.toLowerCase());
      }

      // Handle numbers
      if (typeof valueA === 'number' && typeof valueB === 'number') {
        return valueA - valueB;
      }

      // Handle dates
      if (valueA instanceof Date && valueB instanceof Date) {
        return valueA.getTime() - valueB.getTime();
      }

      // Handle date strings (timestamps)
      if (typeof valueA === 'string' && typeof valueB === 'string') {
        const dateA = new Date(valueA);
        const dateB = new Date(valueB);
        if (!isNaN(dateA.getTime()) && !isNaN(dateB.getTime())) {
          return dateA.getTime() - dateB.getTime();
        }
      }

      // Default string comparison
      return String(valueA).toLowerCase().localeCompare(String(valueB).toLowerCase());
    };
  }

  /**
   * Compare two rows using this column's comparison function
   * @param {Object} a - First row
   * @param {Object} b - Second row
   * @param {string} direction - Sort direction ('asc' or 'desc')
   * @returns {number} - Comparison result
   */
  compare(a, b, direction = 'asc') {
    const result = this.compareFn(a, b);
    return direction === 'desc' ? -result : result;
  }
}

/**
 * Helper function to create a simple text column
 * @param {string} key - Column key
 * @param {string} title - Column title
 * @param {Function} accessor - Data accessor function
 * @param {Object} options - Additional options
 * @returns {Column} - New Column instance
 */
export function createTextColumn(key, title, accessor, options = {}) {
  return new Column({
    key,
    title,
    accessor,
    dataType: 'text',
    ...options
  });
}

/**
 * Helper function to create a date column
 * @param {string} key - Column key
 * @param {string} title - Column title
 * @param {Function} accessor - Data accessor function
 * @param {Object} options - Additional options
 * @returns {Column} - New Column instance
 */
export function createDateColumn(key, title, accessor, options = {}) {
  return new Column({
    key,
    title,
    accessor,
    dataType: 'date',
    renderer: (value) => {
      if (!value) return 'Unknown';
      try {
        return new Date(value).toLocaleString();
      } catch (e) {
        return value;
      }
    },
    compareFn: (a, b) => {
      const valueA = accessor(a);
      const valueB = accessor(b);
      
      if (!valueA && !valueB) return 0;
      if (!valueA) return 1;
      if (!valueB) return -1;
      
      const dateA = new Date(valueA);
      const dateB = new Date(valueB);
      
      if (isNaN(dateA.getTime()) && isNaN(dateB.getTime())) return 0;
      if (isNaN(dateA.getTime())) return 1;
      if (isNaN(dateB.getTime())) return -1;
      
      return dateA.getTime() - dateB.getTime();
    },
    ...options
  });
}

/**
 * Helper function to create an enum column
 * @param {string} key - Column key
 * @param {string} title - Column title
 * @param {Function} accessor - Data accessor function
 * @param {Array} enumOptions - Array of enum values for filtering
 * @param {Object} options - Additional options
 * @returns {Column} - New Column instance
 */
export function createEnumColumn(key, title, accessor, enumOptions = [], options = {}) {
  return new Column({
    key,
    title,
    accessor,
    dataType: 'enum',
    enumOptions,
    ...options
  });
}

/**
 * Helper function to create a custom rendered column
 * @param {string} key - Column key
 * @param {string} title - Column title
 * @param {Function} accessor - Data accessor function
 * @param {Function} renderer - Custom renderer function
 * @param {Object} options - Additional options
 * @returns {Column} - New Column instance
 */
export function createCustomColumn(key, title, accessor, renderer, options = {}) {
  return new Column({
    key,
    title,
    accessor,
    renderer,
    ...options
  });
} 